import { Component } from '@angular/core';
import { Color, DateFormat, Modal } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { InvoiceSummary } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { ModalService } from '@lsq-frontend/common-ui';

@Component({
    selector: 'revoke-invoice-modal',
    template: `
        <div class="modal-content revoke-invoice-modal">
            <div class="modal-header">
                <h1>{{ modal?.title }}</h1>
                <lsq-button
                    *ngIf="!isSubmitting"
                    appearance="transparent"
                    class="close"
                    ariaLabel="Close"
                    (buttonClick)="modalService.closeModal()"
                >
                    <lsq-icon aria-hidden="true" [type]="icons.Close" [color]="color.Black">
                    </lsq-icon>
                </lsq-button>
            </div>
            <div class="modal-body" *ngIf="isSubmitting">
                <lsq-circle-loader></lsq-circle-loader>
            </div>
            <div class="modal-body" *ngIf="!isSubmitting">
                <div class="revoke-invoice-details">
                    <table>
                        <tr>
                            <td class="invoice-detail-label" i18n>Supplier Name:</td>
                            <td>{{ buyerInvoice.sellerName }}</td>
                        </tr>
                        <ng-container *ngIf="isGroup">
                            <tr>
                                <td class="invoice-detail-label" i18n>Group ID:</td>
                                <td>{{ buyerInvoice.groupId }}</td>
                            </tr>
                        </ng-container>
                        <ng-container *ngIf="!isGroup">
                            <tr>
                                <td class="invoice-detail-label" i18n>Invoice #:</td>
                                <td>{{ buyerInvoice.invoiceNumber }}</td>
                            </tr>
                        </ng-container>
                        <ng-container *ngIf="isGroup">
                            <tr>
                                <td class="invoice-detail-label" i18n>Invoice #'s:</td>
                                <td>{{ getInvoiceNumberList() }}</td>
                            </tr>
                        </ng-container>
                        <ng-container *ngIf="!isGroup">
                            <tr>
                                <td class="invoice-detail-label" i18n>Invoice Date:</td>
                                <td>
                                    {{ buyerInvoice.invoiceDate | lsqDate: dateFormat.LsqShort }}
                                </td>
                            </tr>
                        </ng-container>
                        <tr>
                            <td class="invoice-detail-label" i18n>Due Date:</td>
                            <td>
                                {{ buyerInvoice.invoiceDueDate | lsqDate: dateFormat.LsqShort }}
                            </td>
                        </tr>
                        <tr>
                            <td class="invoice-detail-label" i18n>Invoice Amount:</td>
                            <td>
                                {{
                                    buyerInvoice.invoiceAmount &&
                                    buyerInvoice.invoiceAmount.cents < 0
                                        ? (buyerInvoice.invoiceAmount | currencyDash)
                                        : (buyerInvoice.finalApprovedAmount | currencyDash)
                                }}
                            </td>
                        </tr>
                    </table>
                </div>
                <div class="resubmission-instructions">
                    <ng-container
                        *ngIf="!isGroup && !isPendingPage && !buyerInvoice.refGroupedInvoiceKey"
                    >
                        <span i18n
                            >Upon confirmation, this invoice will be set to a "Revoked" status. The
                            invoice will no longer be available for early payment to the supplier.
                            You can resubmit this invoice at any time.</span
                        >
                    </ng-container>
                    <ng-container
                        *ngIf="
                            !isGroup &&
                            ((isPendingPage && buyerInvoice.groupId) ||
                                buyerInvoice.refGroupedInvoiceKey) &&
                            buyerInvoice.invoiceAmount &&
                            buyerInvoice.invoiceAmount.cents >= 0
                        "
                    >
                        <span i18n
                            >Upon confirmation, this invoice in the credit memo group will be set to
                            a "Revoked" status. The value amount of the invoice will change and
                            could affect the eligibility. This process can take several minutes to
                            complete and may appear in a "Processing" status. You can resubmit this
                            invoice at any time.</span
                        >
                    </ng-container>
                    <ng-container
                        *ngIf="
                            !isGroup &&
                            ((isPendingPage && buyerInvoice.groupId) ||
                                buyerInvoice.refGroupedInvoiceKey) &&
                            buyerInvoice.invoiceAmount &&
                            buyerInvoice.invoiceAmount.cents < 0
                        "
                    >
                        <span i18n
                            >Upon confirmation, this credit memo will be set to a "Revoked" status.
                            If this is is the last credit memo all invoices within this grouping
                            will become standalone invoices. This process can take several minutes
                            to complete and may appear in a "Processing" status. You can resubmit
                            this invoice at any time.</span
                        >
                    </ng-container>
                    <ng-container *ngIf="isGroup">
                        <span i18n
                            >Upon confirmation, all invoices within this grouping will be set to a
                            "Revoked" status. The invoices will no longer be available for early
                            payment to the supplier. This process can take several minutes to
                            complete and may appear in a "Processing" status. You can resubmit any
                            of the invoices at any time.</span
                        >
                    </ng-container>
                </div>
            </div>
            <div class="modal-footer ok-btn-showing" *ngIf="!isSubmitting">
                <div class="modal-footer-left">
                    <lsq-button
                        appearance="secondary"
                        (buttonClick)="modalService.closeModal()"
                        class="cancel-btn pull-left"
                        i18n
                    >
                        Cancel
                    </lsq-button>
                </div>
                <div class="modal-footer-right">
                    <lsq-button appearance="primary" (buttonClick)="confirmRevoke()" i18n>
                        Confirm
                    </lsq-button>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./revoke-invoice-modal.component.scss']
})
export class RevokeInvoiceModalComponent {
    public modal: Modal = this.modalService.modal;
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly dateFormat = DateFormat;
    public isSubmitting = false;
    public buyerInvoice: InvoiceSummary = this.modal.data.buyerInvoice;
    public isPendingPage: boolean = this.modal.data.isPendingPage;
    public isGroup: boolean = this.buyerInvoice.isGroup;
    public childInvoices: InvoiceSummary[] = this.isGroup ? this.modal.data.childInvoices : [];

    constructor(public modalService: ModalService) {}

    public confirmRevoke(): void {
        this.isSubmitting = true;
        this.modal.onOkClick?.();
    }

    public getInvoiceNumberList(): string {
        let plusLeftOver = '';
        let childInvoiceSubset: InvoiceSummary[];

        if (this.childInvoices.length > 5) {
            childInvoiceSubset = this.childInvoices.slice(0, 5);
            plusLeftOver = '... +' + (this.childInvoices.length - 5);
        } else {
            childInvoiceSubset = this.childInvoices;
        }

        return (
            childInvoiceSubset.map(({ invoiceNumber }) => invoiceNumber).join(', ') + plusLeftOver
        );
    }
}
